import { useEffect, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { CheckIcon, LoaderCircleIcon, SendIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';
import * as yup from 'yup';

import PortalHeading from '../../components/pages/customer-portal/heading.jsx';
import Error from '../../components/feedback/error.jsx';
import Loading from '../../components/feedback/loading.jsx';
import {
  turnstileStatusState,
  TurnstileWidget,
} from '../../components/turnstile-widget.jsx';
import { Button } from '../../components/ui/button.jsx';
import { Card } from '../../components/ui/card.jsx';
import FormFooter from '../../components/ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form.jsx';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '../../components/ui/input-otp.jsx';
import { PasswordInput } from '../../components/ui/input-password.jsx';
import { msCatalystApi } from '../../services/api/ms-catalyst-api.js';

export const PasswordDefine = () => {
  const { tId } = useParams();

  const navigate = useNavigate();
  const [isPasswordDefined, setIsPasswordDefined] = useState(false);
  const [isOtpValidated, setIsOtpValidated] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const resendIntervalRef = useRef(null);
  const formRef = useRef();
  const turnstileRef = useRef();
  const { status: turnstileStatus, setStatus: setTurnstileStatus } =
    useSnapshot(turnstileStatusState);

  const formValidateDefinePasswordOtp = useForm({
    mode: 'onChange',
    defaultValues: {
      otp: '',
    },
    resolver: yupResolver(
      yup.object().shape({
        otp: yup
          .string()
          .min(6, 'O código tem de possuir 6 números')
          .required('Por favor insira o código que recebeu no seu email'),
      }),
    ),
  });

  const formDefinePassword = useForm({
    mode: 'onChange',
    resolver: yupResolver(
      yup.object({
        password: yup
          .string()
          .required('Por favor insira a sua palavra-passe.')
          .min(
            8,
            ({ min }) =>
              `A palavra-passe deve conter pelo menos ${min} caracteres`,
          )
          .max(
            250,
            ({ max }) =>
              `A palavra-passe não deve ultrapassar os ${max} caracteres`,
          )
          .matches(
            /\w*[A-Z]\w*/,
            'A palavra-passe deve conter pelo menos 1 letra maiúscula',
          )
          .matches(
            /\w*[a-z]\w*/,
            'A palavra-passe deve conter pelo menos 1 letra minúscula',
          )
          .matches(/\d/, 'A palavra-passe deve conter pelo menos 1 número')
          .matches(
            /[!+@#$%^&*()\-_"=+{}; :,<.>]/,
            'A palavra-passe deve conter pelo menos 1 um caracter especial',
          ),
        confirmPassword: yup
          .string()
          .required('Por favor insira a sua palavra-passe novamente.')
          .oneOf([yup.ref('password')], 'As palavras-passes não coincidem.'),
      }),
    ),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const queryDefinePasswordVerifySession = useQuery({
    queryKey: ['define-password-session'],
    queryFn: async () => {
      const { data: getDefinePasswordSession } = await msCatalystApi.post(
        `/auth/define-password/session`,
        { t: tId },
      );

      return getDefinePasswordSession.data;
    },
  });

  const mutationVerifyDefinePasswordOtp = useMutation({
    mutationFn: async (data) => {
      const { data: loggedInUser } = await msCatalystApi.post(
        `/auth/define-password/verify-otp`,
        {
          t: tId,
          otp: data.otp,
          turnstile_token: data.turnstileToken,
        },
      );

      return loggedInUser.data;
    },
    onSuccess: (data) => {
      setIsOtpValidated(true);
    },
    onError: (err) => {
      if (
        err instanceof AxiosError &&
        err.response.data?.message === 'OTP not found or expired.'
      ) {
        toast.error(
          'O código de definição de palavra-passe que inseriu não existe ou já expirou',
          {
            description:
              'Por favor peça um novo código ao carregar no botão "Reenviar Código"',
          },
        );

        return;
      } else {
        toast.error(
          'Ocorreu um erro ao validar o código de definição de palavra-passe',
          {
            description: 'Por favor tente novamente mais tarde',
          },
        );
      }

      setTurnstileStatus('pending');
      turnstileRef.current?.reset();
    },
  });

  const mutationResendDefinePasswordOtp = useMutation({
    mutationFn: async (data) => {
      await msCatalystApi.post(`/auth/define-password/otp`, {
        t: tId,
      });

      return true;
    },
    onSuccess: (data) => {
      setResendCooldown(60);

      toast.success(
        'Email com novo código único de acesso enviado com sucesso!',
      );
    },
    onError: (err) => {
      toast.error('Ocorreu um erro ao renviar o código único de acesso', {
        description: 'Por favor tente novamente mais tarde',
      });
    },
  });

  // useEffect for the resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      resendIntervalRef.current = setInterval(() => {
        setResendCooldown((prevCooldown) => {
          if (prevCooldown <= 1) {
            clearInterval(resendIntervalRef.current);
            return 0; // Resets cooldown
          }
          return prevCooldown - 1;
        });
      }, 1000);
    } else if (resendIntervalRef.current) {
      // Clear interval if cooldown is set to 0 externally
      clearInterval(resendIntervalRef.current);
    }

    return () => {
      // Cleanup interval on component unmount or if resendCooldown changes
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
      }
    };
  }, [resendCooldown]);

  async function onSubmitDefinePasswordVerifyOtp(data) {
    const formData = new FormData(formRef.current);
    const turnstileToken = formData.get('cf-turnstile-response');

    if (!turnstileToken) {
      toast.error('Erro ao confimar código único de acesso', {
        description:
          'Não foi possível determinar com sucesso a sua veracidade como humano',
      });

      return;
    }

    mutationVerifyDefinePasswordOtp.mutate({
      otp: data.otp,
      turnstileToken,
    });
  }

  async function onSubmit(data) {
    try {
      await msCatalystApi.post('/auth/define-password/conclude', {
        t: tId,
        password: data.password,
      });

      setIsPasswordDefined(true);

      setTimeout(() => {
        navigate('/auth/login');
      }, 4000);
    } catch (err) {
      toast.error('Erro ao definir palavra-passe');
    }
  }

  // Error defining password
  console.log(
    'queryDefinePasswordVerifySession.error',
    queryDefinePasswordVerifySession.error,
  );

  return (
    <Card className="p-4 sm:p-10">
      {queryDefinePasswordVerifySession.isLoading ? (
        <Loading />
      ) : queryDefinePasswordVerifySession.isError &&
        queryDefinePasswordVerifySession.error?.response?.data?.message ===
          'Error defining password' ? (
        <div className="flex flex-col items-center text-center">
          <SendIcon className="size-16 text-muted-foreground" />
          <PortalHeading className="pt-6 !text-xl">
            Link de definição de palavra-passe expirado
          </PortalHeading>

          <div className="max-w-md py-4 text-sm text-muted-foreground">
            <p>
              Peça um novo email de definição de palavra-passe para continuar a
              ativação da sua conta.
            </p>
          </div>

          <Button asChild variant="portal">
            <Link to="/auth/password/recover">Pedir novo link</Link>
          </Button>
        </div>
      ) : queryDefinePasswordVerifySession.isError ? (
        <Error />
      ) : isPasswordDefined ? (
        <div className="flex flex-col items-center">
          <CheckIcon className="size-40 text-success" />
          <PortalHeading className="!text-2xl">
            Palavra-passe definida com sucesso!
          </PortalHeading>

          <div className="py-4 text-center">
            <p className="mt-2">
              Poderá agora aceder à plataforma de campanhas do MAR Shopping
              Matosinhos!
            </p>
            <p>
              Por favor aguarde enquanto o redirecionamos para a página de
              início de sessão.
            </p>
          </div>
        </div>
      ) : !isOtpValidated ? (
        <>
          <div className="mb-2 flex flex-col space-y-2 pb-4 text-left">
            <PortalHeading className="!text-xl">
              Definir Palavra-passe
            </PortalHeading>
            <p className="text-sm text-muted-foreground">
              Por motivos de segurança necessitamos que insira o código que foi
              enviado para o seu e-mail
            </p>
          </div>

          <Form
            key="define-password-validate-otp"
            {...formValidateDefinePasswordOtp}
          >
            <form
              ref={formRef}
              name="define-password-validate-otp"
              onSubmit={formValidateDefinePasswordOtp.handleSubmit(
                onSubmitDefinePasswordVerifyOtp,
              )}
            >
              <div className="py-4">
                <FormField
                  control={formValidateDefinePasswordOtp.control}
                  name="otp"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Código de definição de palavra-passe
                      </FormLabel>
                      <FormControl>
                        <InputOTP maxLength={6} {...field}>
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                          </InputOTPGroup>

                          <InputOTPSeparator />

                          <InputOTPGroup>
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                          </InputOTPGroup>

                          <InputOTPSeparator />

                          <InputOTPGroup>
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </FormControl>

                      <FormMessage />

                      <Button
                        type="button"
                        variant="link"
                        className="px-0 pt-1 text-xs text-muted-foreground"
                        disabled={
                          mutationVerifyDefinePasswordOtp.isPending ||
                          mutationResendDefinePasswordOtp.isPending ||
                          resendCooldown > 0
                        }
                        onClick={() => mutationResendDefinePasswordOtp.mutate()}
                        isLoading={mutationResendDefinePasswordOtp.isPending}
                      >
                        {resendCooldown > 0 ? (
                          <LoaderCircleIcon className="animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite]" />
                        ) : (
                          <SendIcon />
                        )}
                        {resendCooldown > 0
                          ? `Reenviar código de definição de palavra-passe em ${resendCooldown}s`
                          : 'Reenviar código de definição de palavra-passe'}
                      </Button>
                    </FormItem>
                  )}
                />
              </div>

              <div className="pt-2">
                <TurnstileWidget ref={turnstileRef} />
              </div>

              <FormFooter className="pt-8">
                <Button
                  variant="portal"
                  type="submit"
                  disabled={
                    mutationVerifyDefinePasswordOtp.isPending ||
                    mutationResendDefinePasswordOtp.isPending ||
                    turnstileStatus === 'pending'
                  }
                  isLoading={mutationVerifyDefinePasswordOtp.isPending}
                >
                  Confirmar
                </Button>
              </FormFooter>
            </form>
          </Form>
        </>
      ) : (
        <>
          <div className="mb-2 flex flex-col space-y-2 pb-6 text-left">
            <PortalHeading className="!text-xl">
              Definir Palavra-passe
            </PortalHeading>
            <p className="text-sm text-muted-foreground">
              Crie uma palavra-passe para acesso à plataforma de campanhas MAR
              Shopping Matosinhos
            </p>
          </div>
          <div className="grid gap-6">
            <Form key="define-password" {...formDefinePassword}>
              <form
                key="define-password"
                className="space-y-6"
                onSubmit={formDefinePassword.handleSubmit(onSubmit)}
              >
                <div className="flex h-[46px] w-full items-center rounded-full border border-input border-portal-foreground !bg-background px-3 py-2 text-sm">
                  {queryDefinePasswordVerifySession.data.email}
                </div>

                <FormField
                  control={formDefinePassword.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem variant="portal">
                      <FormLabel required={true}>Palavra-passe</FormLabel>
                      <FormControl>
                        <PasswordInput {...field} autoComplete="new-password" />
                      </FormControl>

                      <FormMessage />

                      <ul className="list-disc pl-8 text-sm text-muted-foreground">
                        <li>
                          A palavra-passe deve conter pelo menos 8 caracteres
                        </li>
                        <li>
                          A palavra-passe não deve ultrapassar os 250 caracteres
                        </li>
                        <li>
                          A palavra-passe deve conter pelo menos uma letra
                          maiúscula e minúscula
                        </li>
                        <li>
                          A palavra-passe deve conter pelo menos um número
                        </li>
                        <li>
                          A palavra-passe deve conter pelo menos um caracter
                          especial
                        </li>
                      </ul>
                    </FormItem>
                  )}
                />

                <FormField
                  control={formDefinePassword.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem variant="portal">
                      <FormLabel required={true}>
                        Confirmar Palavra-passe
                      </FormLabel>
                      <FormControl>
                        <PasswordInput {...field} autoComplete="new-password" />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormFooter className="pt-4">
                  <Button
                    type="submit"
                    variant="portal"
                    disabled={
                      !formDefinePassword.formState.isDirty ||
                      formDefinePassword.formState.isSubmitting
                    }
                    isLoading={formDefinePassword.formState.isSubmitting}
                  >
                    Confirmar
                  </Button>
                </FormFooter>
              </form>
            </Form>
          </div>
        </>
      )}
    </Card>
  );
};
